import { useState, useEffect, useRef, useCallback } from "react";
import normalizeResumeData from "../utils/normalizeResumeData";

export const useAts = ({
  authToken,
  resumeData,
  setResumeData,
  getAuthHeaders,
  credits,
  setCredits,
  setShowPricing,
  setReply,
  replyEndRef,
  triggerLightningAnimation,
  setToast,
}) => {
  const [atsScore, setAtsScore] = useState(null); // { score, breakdown, suggestions }
  const [atsExpanded, setAtsExpanded] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [isFetchingQuality, setIsFetchingQuality] = useState(false);
  const [careerMemory, setCareerMemory] = useState(null);

  const [isBoosting, setIsBoosting] = useState(false);
  const [boostReport, setBoostReport] = useState(null);
  const [boostProgress, setBoostProgress] = useState(null);
  const [preBoostScore, setPreBoostScore] = useState(null);
  const [postBoostScore, setPostBoostScore] = useState(null);
  const [preBoostResume, setPreBoostResume] = useState(null);
  const preBoostResumeRef = useRef(null);

  const calculateAtsScoreForData = useCallback(async (dataToScore) => {
    if (!authToken) return null;
    try {
      const res = await fetch("/api/ats/score", {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify(dataToScore)
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.error("Failed to calculate ATS score for data:", err);
    }
    return null;
  }, [authToken, getAuthHeaders]);

  const atsDebounceRef = useRef(null);

  const fetchQualityScore = useCallback(async () => {
    if (!authToken) return;
    setIsFetchingQuality(true);
    try {
      const res = await fetch("/api/ats/quality", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setQualityScore(data);
      }
    } catch (err) {
      console.error("Failed to fetch quality score:", err);
    } finally {
      setIsFetchingQuality(false);
    }
  }, [authToken]);

  const fetchCareerMemory = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch("/api/career/memory", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCareerMemory(data);
      }
    } catch (err) {
      console.error("Failed to fetch career memory:", err);
    }
  }, [authToken]);

  const updateCareerMemory = async (newData) => {
    if (!authToken) return;
    try {
      const res = await fetch("/api/career/memory", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(newData),
      });
      if (res.ok) {
        if (setToast) setToast({ message: "Career facts database updated!", type: "success" });
        fetchCareerMemory();
      } else {
        const data = await res.json();
        alert(`Failed to update career facts: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Error updating career memory:", err);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchQualityScore();
      fetchCareerMemory();
    }
  }, [authToken, fetchQualityScore, fetchCareerMemory]);

  useEffect(() => {
    if (authToken && resumeData) {
      fetchQualityScore();
    }
  }, [resumeData, authToken, fetchQualityScore]);

  const fetchAtsScore = useCallback(async () => {
    if (!authToken) return;
    if (!resumeData?.name && !resumeData?.experience?.length) return;
    clearTimeout(atsDebounceRef.current);
    atsDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch("/api/ats/score", { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setAtsScore(data);
        }
      } catch {}
    }, 1500);
  }, [authToken, resumeData, getAuthHeaders]);

  useEffect(() => {
    fetchAtsScore();
  }, [fetchAtsScore]);

  const handleAIBoost = async () => {
    if (!resumeData || credits < 10) {
      if (credits < 10 && credits !== null) setShowPricing(true);
      return;
    }
    setBoostReport('confirm');
  };

  const handleAIBoostConfirmed = async () => {
    setBoostReport(null);
    setIsBoosting(true);
    setBoostProgress({ currentStep: 0, totalSteps: 0, steps: [], done: false });
    
    // Cache original resume data and score before starting boost
    const cached = JSON.parse(JSON.stringify(resumeData));
    preBoostResumeRef.current = cached;
    setPreBoostResume(cached);
    setPreBoostScore(atsScore?.score || 0);
    setPostBoostScore(null);

    try {
      const res = await fetch("/api/resume/ats-boost/stream", {
        method: "POST",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (err.code === 'no_credits') {
          setShowPricing(true);
          setBoostProgress(null);
          return;
        }
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          switch (event.type) {
            case 'boost_start':
              setBoostProgress({
                currentStep: 0,
                totalSteps: event.total_steps,
                steps: event.steps.map((s) => ({ ...s, status: 'pending' })),
                done: false,
              });
              break;

            case 'boost_step':
              setBoostProgress((prev) => {
                if (!prev) return prev;
                const activeIdx = prev.steps.findIndex((s) => s.id === event.section);
                return {
                  ...prev,
                  currentStep: event.step,
                  steps: prev.steps.map((s, idx) => ({
                    ...s,
                    status: idx < activeIdx ? 'done' : idx === activeIdx ? 'active' : s.status,
                  })),
                };
              });
              break;

            case 'boost_section_done':
              setResumeData(normalizeResumeData(event.resume_data));
              setBoostProgress((prev) =>
                prev
                  ? {
                      ...prev,
                      steps: prev.steps.map((s) =>
                        s.id === event.section ? { ...s, status: 'done' } : s
                      ),
                    }
                  : prev
              );
              break;

            case 'boost_step_error':
              setBoostProgress((prev) =>
                prev
                  ? {
                      ...prev,
                      steps: prev.steps.map((s) =>
                        s.id === event.section ? { ...s, status: 'error' } : s
                      ),
                    }
                  : prev
              );
              break;

            case 'boost_done':
              const finalData = normalizeResumeData(event.resume_data);
              // Calculate post-boost score asynchronously
              calculateAtsScoreForData(finalData).then((scoreResult) => {
                if (scoreResult) {
                  setPostBoostScore(scoreResult.score || 0);
                }
              });

              setResumeData(finalData);
              setBoostProgress((prev) =>
                prev
                  ? {
                      ...prev,
                      done: true,
                      currentStep: prev.totalSteps,
                      steps: prev.steps.map((s) => ({
                        ...s,
                        status: s.status === 'error' ? 'error' : 'done',
                      })),
                    }
                  : prev
              );
              setTimeout(() => {
                setBoostProgress(null);
                // Store final boosted resume in the boostReport state for Accept/Reject review
                setBoostReport({
                  ...(event.boost_report || {}),
                  resume_data: finalData
                });
                setReply((prev) => [
                  ...prev,
                  {
                    role: 'assistant',
                    content: '✨ Resume fully boosted with AI! Check the Boost Report for details on what was improved.',
                  },
                ]);
                setTimeout(() => replyEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
              }, 1800);
              break;

            case 'boost_credits':
              setCredits(event.credits_remaining);
              break;

            case 'boost_error':
              setBoostProgress(null);
              if (event.code === 'no_credits') setShowPricing(true);
              else setBoostReport({ _error: event.message || 'Boost failed. Please try again.' });
              break;

            default:
              break;
          }
        }
      }
    } catch (err) {
      console.error('[Boost] SSE error:', err);
      setBoostProgress(null);
      setBoostReport({
        _error: err.message || 'Network error. Could not connect to AI Boost service.',
      });
    } finally {
      setIsBoosting(false);
    }
  };

  const handleAcceptBoost = async (boostedData) => {
    if (!authToken || !boostedData) return;
    try {
      const docId = boostedData.id || 'master';
      const docType = (docId && docId !== 'master') ? 'variant' : 'master';
      
      const res = await fetch('/api/workspace/document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          action: 'save_resume',
          payload: boostedData,
          active_document: {
            id: docId,
            type: docType
          }
        })
      });
      if (res.ok) {
        setResumeData(boostedData);
        setBoostReport(null);
        if (setToast) setToast({ message: "AI Boost accepted and saved!", type: "success" });
      } else {
        alert("Failed to save boosted resume.");
      }
    } catch (err) {
      console.error("Error saving boosted resume:", err);
    }
  };

  const handleRejectBoost = () => {
    if (preBoostResumeRef.current) {
      setResumeData(preBoostResumeRef.current);
    }
    setBoostReport(null);
    if (setToast) setToast({ message: "Boosted changes discarded.", type: "info" });
  };

  return {
    atsScore,
    setAtsScore,
    atsExpanded,
    setAtsExpanded,
    qualityScore,
    setQualityScore,
    isFetchingQuality,
    careerMemory,
    setCareerMemory,
    fetchQualityScore,
    fetchCareerMemory,
    updateCareerMemory,
    isBoosting,
    boostReport,
    setBoostReport,
    boostProgress,
    handleAIBoost,
    handleAIBoostConfirmed,
    preBoostScore,
    postBoostScore,
    preBoostResume,
    handleAcceptBoost,
    handleRejectBoost,
  };
};
